// Journey page constants: the severity scale, the indicator table metadata and
// the report presets. Kept out of the components so the aggregation and the
// wording can change without touching the rendering.
import {
	Footprints,
	Activity,
	Flame,
	Route,
	Gauge,
	Thermometer,
	Brain,
	BedSingle,
	Moon,
	Percent,
	HeartPulse,
} from 'lucide-react';

// Five severity levels, 1 = mildest, 5 = most severe. The scale ends on the
// app's coral so the worst rating reads as the brand's alert colour.
export const SEVERITY_SCALE = {
	1: { color: '#4CAF88', label: 'Very mild' },
	2: { color: '#A8CF7A', label: 'Mild' },
	3: { color: '#F5C563', label: 'Moderate' },
	4: { color: '#EF9A5C', label: 'Severe' },
	5: { color: '#DE6957', label: 'Very severe' },
};

export const SEVERITY_LEVELS = [1, 2, 3, 4, 5];

// The four buckets a rating log falls into, in the order the week view stacks them.
export const DAY_PERIODS = [
	{ key: 'morning', label: 'Morning', hint: '6am – 12pm' },
	{ key: 'afternoon', label: 'Afternoon', hint: '12pm – 6pm' },
	{ key: 'evening', label: 'Evening', hint: '6pm – 12am' },
	{ key: 'night', label: 'Night', hint: '12am – 6am' },
];

// The report's rolling windows. Not calendar periods: "last 7 days" on a
// Wednesday is not "this week".
export const REPORT_PRESETS = [
	{ key: 'last_7_days', label: 'Last 7 days' },
	{ key: 'last_30_days', label: 'Last 30 days' },
	{ key: 'last_3_months', label: 'Last 3 months' },
	{ key: 'last_6_months', label: 'Last 6 months' },
	{ key: 'custom', label: 'Custom range' },
];

// The report is printed in one pass and never paginates; the API rejects
// anything longer with a 422.
export const MAX_REPORT_DAYS = 366;

const formatNumber = (value, digits = 0) =>
	Number(value).toLocaleString('en-US', {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	});

const formatDuration = (minutes) => {
	const total = Math.round(Number(minutes));
	const hours = Math.floor(total / 60);
	const mins = total % 60;
	return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// One entry per column on device_daily_summaries, in the order the design's
// sidebar lists them. `betterDirection` is how the frontend colours a trend:
// the API only reports which way the number moved, not whether that is good.
// null means neither direction is better, so the trend stays neutral.
const INDICATORS = [
	{
		key: 'steps',
		label: 'Steps',
		icon: Footprints,
		betterDirection: 'up',
		format: (v) => formatNumber(v),
		unit: '/ day',
	},
	{
		key: 'active_minutes',
		label: 'Active minutes',
		icon: Activity,
		betterDirection: 'up',
		format: (v) => formatDuration(v),
		unit: '/ day',
	},
	{
		key: 'calories_burned',
		label: 'Calories burned',
		icon: Flame,
		betterDirection: null,
		format: (v) => formatNumber(v),
		unit: 'kcal / day',
	},
	{
		key: 'distance_meters',
		label: 'Distance',
		icon: Route,
		betterDirection: 'up',
		format: (v) => formatNumber(Number(v) / 1000, 1),
		unit: 'km / day',
	},
	{
		key: 'vo2_max',
		label: 'VO₂ max',
		icon: Gauge,
		betterDirection: 'up',
		format: (v) => formatNumber(v, 1),
		unit: 'mL/kg·min',
	},
	{
		key: 'skin_temp_variation',
		label: 'Skin temp. variation',
		icon: Thermometer,
		betterDirection: null,
		format: (v) => `${Number(v) > 0 ? '+' : ''}${formatNumber(v, 1)}`,
		unit: '°C',
	},
	{
		key: 'meditation_minutes',
		label: 'Meditation',
		icon: Brain,
		betterDirection: 'up',
		format: (v) => formatDuration(v),
		unit: '/ day',
	},
	{
		key: 'sleep_minutes',
		label: 'Sleep',
		icon: BedSingle,
		betterDirection: 'up',
		format: (v) => formatDuration(v),
		unit: '/ night',
	},
	{
		key: 'sleep_score',
		label: 'Sleep score',
		icon: Moon,
		betterDirection: 'up',
		format: (v) => formatNumber(v, 1),
		unit: '/ 100',
	},
	{
		key: 'sleep_efficiency',
		label: 'Sleep efficiency',
		icon: Percent,
		betterDirection: 'up',
		format: (v) => formatNumber(v, 1),
		unit: '%',
	},
	{
		key: 'resting_heart_rate',
		label: 'Resting heart rate',
		icon: HeartPulse,
		betterDirection: 'down',
		format: (v) => formatNumber(v),
		unit: 'bpm',
	},
];

export const INDICATORS_BY_KEY = INDICATORS.reduce((map, indicator) => {
	map[indicator.key] = indicator;
	return map;
}, {});

// Sleep stages (deep / light / REM / awake) are deliberately absent: there is
// no column for them, and a breakdown does not fit a single-average row.
